import { Brain, ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "@bigpp/ui/lib/utils";

import type { ChatMsg, ProgressPart, ToolCallCard } from "../agent/useAgentRun.ts";
import { JsxPreview } from "./JsxPreview.tsx";
import { Plan } from "./Plan.tsx";
import { Reasoning } from "./Reasoning.tsx";
import { TaskRow } from "./TaskRow.tsx";
import { labelFor } from "./ToolCard.tsx";

type AssistantMsg = Extract<ChatMsg, { role: "assistant" }>;

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms.toString()}ms`;
  const s = Math.round(ms / 100) / 10;
  return `${s.toString()}s`;
};

const useTick = (active: boolean): void => {
  const [, setNow] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      setNow((n) => n + 1);
    }, 250);
    return () => {
      clearInterval(id);
    };
  }, [active]);
};

// Render rules:
//   - reasoning parts get their own (small, collapsible) Reasoning block
//   - create_plan / jsx_preview render inline as full-width components
//   - everything else collapses into a compact TaskRow inside the outer
//     "Thinking" panel.
// Splitting it this way makes Plan/Preview prominent (the user wants to see
// them) while the firehose of execute_office_js_code/peek_slide rows stays out of the
// way until expanded.
export function Progress({ msg }: { msg: AssistantMsg }) {
  const isRunning = !msg.finished;
  useTick(isRunning);

  if (msg.progressParts.length === 0) return null;

  const elapsedMs = (msg.finishedAt ?? Date.now()) - msg.startedAt;
  const toolById = new Map(msg.toolCalls.map((tc) => [tc.toolCallId, tc]));

  // Merge every reasoning chunk in the message into a single collapsible.
  // The agent alternates reasoning ↔ tool calls, so a long turn used to
  // produce a stack of "Thought for Ns" rows — noisy without adding info.
  const reasoningParts = msg.progressParts.filter(
    (p): p is Extract<ProgressPart, { kind: "reasoning" }> => p.kind === "reasoning",
  );
  const mergedReasoningText = reasoningParts
    .map((p) => p.text.trim())
    .filter((t) => t.length > 0)
    .join("\n\n");
  const firstReasoningIndex = msg.progressParts.findIndex(
    (p) => p.kind === "reasoning",
  );

  const rowParts: ProgressPart[] = [];
  const inlineParts: ProgressPart[] = [];
  for (const part of msg.progressParts) {
    if (part.kind === "reasoning") continue;
    const tc = toolById.get(part.toolCallId);
    if (!tc) continue;
    if (tc.toolName === "create_plan" || tc.toolName === "jsx_preview") {
      inlineParts.push(part);
    } else {
      rowParts.push(part);
    }
  }

  const reasoningBlock = mergedReasoningText.length > 0 && (
    <Reasoning
      key="reasoning"
      text={mergedReasoningText}
      streaming={isRunning}
      elapsedLabel={
        msg.finished ? `Thought for ${formatDuration(elapsedMs)}` : undefined
      }
    />
  );

  return (
    <div className="flex flex-col gap-1.5">
      {firstReasoningIndex === 0 && reasoningBlock}
      {inlineParts.map((part) => {
        if (part.kind !== "tool") return null;
        const tc = toolById.get(part.toolCallId);
        if (!tc) return null;
        if (tc.toolName === "create_plan") {
          return <Plan key={`t:${tc.toolCallId}`} title={tc.title} items={tc.items} />;
        }
        if (tc.toolName === "jsx_preview") {
          return <JsxPreview key={`t:${tc.toolCallId}`} title={tc.title} html={tc.html} />;
        }
        return null;
      })}
      {firstReasoningIndex > 0 && reasoningBlock}
      {rowParts.length > 0 && (
        <TaskGroup
          parts={rowParts}
          toolById={toolById}
          isRunning={isRunning}
          elapsedMs={elapsedMs}
        />
      )}
    </div>
  );
}

function TaskGroup({
  parts,
  toolById,
  isRunning,
  elapsedMs,
}: {
  parts: ProgressPart[];
  toolById: Map<string, ToolCallCard>;
  isRunning: boolean;
  elapsedMs: number;
}) {
  const [open, setOpen] = useState(false);
  const headerLabel = isRunning
    ? (latestRowLabel(parts, toolById) ?? "Working…")
    : `${parts.length.toString()} step${parts.length === 1 ? "" : "s"} · ${formatDuration(elapsedMs)}`;
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-xs hover:bg-[var(--color-secondary)] transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          <Brain
            className={cn(
              "size-3.5 shrink-0 text-[var(--color-muted-foreground)]",
              isRunning && "animate-pulse",
            )}
          />
          <span
            className={cn(
              "truncate text-[var(--color-foreground)]",
              isRunning && "shimmer-text",
            )}
          >
            {headerLabel}
          </span>
        </span>
        {open ? (
          <ChevronDown className="size-3.5 text-[var(--color-muted-foreground)]" />
        ) : (
          <ChevronRight className="size-3.5 text-[var(--color-muted-foreground)]" />
        )}
      </button>
      {open && (
        <ul className="border-t border-[var(--color-border)] flex flex-col">
          {parts.map((p) => {
            if (p.kind !== "tool") return null;
            const tc = toolById.get(p.toolCallId);
            if (!tc) return null;
            const isLast = parts[parts.length - 1] === p;
            return (
              <TaskRow
                key={`t:${tc.toolCallId}`}
                tc={tc}
                shimmer={isRunning && isLast}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}

const latestRowLabel = (
  parts: ProgressPart[],
  toolById: Map<string, ToolCallCard>,
): string | null => {
  const last = parts[parts.length - 1];
  if (!last || last.kind !== "tool") return null;
  const tc = toolById.get(last.toolCallId);
  return tc ? labelFor(tc) : null;
};
